import { getContext, isContextAvailable } from '@/miniappContext';

/**
 * Reads the given keys from the SDK location store. Array values collapse to
 * their first element; values are trimmed and blanks are dropped.
 */
export function readDeeplinkParams<K extends string>(keys: readonly K[]): Partial<Record<K, string>> {
  const result: Partial<Record<K, string>> = {};
  if (!isContextAvailable()) return result;
  try {
    const { params } = getContext().general.location.getState();
    keys.forEach(key => {
      const raw = params?.[key];
      const value = (Array.isArray(raw) ? raw[0] : raw)?.trim();
      if (value) result[key] = value;
    });
  } catch {
    // Location store unavailable (standalone) — nothing to read.
  }
  return result;
}

/**
 * One-shot cleanup: removes consumed keys from the location store so they
 * don't re-apply on re-open / navigation. No-op when nothing to strip.
 */
export function stripDeeplinkParams(keys: readonly string[]): void {
  if (!isContextAvailable()) return;
  try {
    const { location } = getContext().general;
    const state = location.getState();
    const present = keys.filter(key => state.params?.[key] !== undefined);
    if (present.length === 0) return;
    const params = Object.fromEntries(Object.entries(state.params).filter(([key]) => !present.includes(key)));
    void location.dispatch({ ...state, params });
  } catch {
    // Location store unavailable (standalone) — nothing to strip.
  }
}
